<template>
  <div class="pdf-scanner">
    <div class="card shadow-sm">
      <div class="card-header bg-light">
        <h5 class="mb-0">
          <i class="fas fa-file-pdf me-2 text-danger"></i>
          PDF Document Scanner
        </h5>
        <small class="text-muted">Check PDF accessibility compliance</small>
      </div>
      <div class="card-body">
        <!-- Drop Zone -->
        <div
          class="drop-zone"
          :class="{
            'drag-over': isDragging,
            'has-file': selectedFile,
            'scanning': isScanning
          }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
          @click="triggerFileInput"
        >
          <input
            type="file"
            ref="fileInput"
            accept=".pdf"
            @change="handleFileSelect"
            class="d-none"
          />

          <div v-if="isScanning" class="text-center">
            <div class="spinner-border text-primary mb-3" role="status">
              <span class="visually-hidden">Scanning...</span>
            </div>
            <p class="mb-0">Analyzing PDF for accessibility issues...</p>
            <small class="text-muted">This may take a moment for large files</small>
          </div>

          <div v-else-if="!selectedFile" class="text-center">
            <i class="fas fa-cloud-upload-alt fa-4x text-muted mb-3"></i>
            <p class="mb-1">Drag & drop a PDF file here</p>
            <p class="text-muted small mb-0">or click to browse</p>
            <p class="text-muted small mt-2">Maximum file size: 50MB</p>
          </div>

          <div v-else class="text-center">
            <i class="fas fa-file-pdf fa-3x text-danger mb-2"></i>
            <p class="mb-1 fw-bold">{{ selectedFile.name }}</p>
            <p class="text-muted small mb-2">{{ formatFileSize(selectedFile.size) }}</p>
            <button
              class="btn btn-sm btn-outline-secondary"
              @click.stop="clearFile"
            >
              <i class="fas fa-times me-1"></i>
              Remove
            </button>
          </div>
        </div>

        <!-- Scan Button -->
        <div v-if="selectedFile && !isScanning && !scanResult" class="text-center mt-3">
          <button class="btn btn-primary btn-lg" @click="scanPdf">
            <i class="fas fa-search me-2"></i>
            Scan for Accessibility Issues
          </button>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="alert alert-danger mt-3">
          <i class="fas fa-exclamation-circle me-2"></i>
          {{ error }}
        </div>

        <!-- Scan Results -->
        <div v-if="scanResult" class="scan-results mt-4">
          <!-- Summary Card -->
          <div class="row mb-4">
            <div class="col-md-4">
              <div class="result-card" :class="getRiskClass(scanResult.risk_level)">
                <div class="result-icon">
                  <i :class="getRiskIcon(scanResult.risk_level)"></i>
                </div>
                <div class="result-label">Risk Level</div>
                <div class="result-value">{{ formatRiskLevel(scanResult.risk_level) }}</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="result-card">
                <div class="result-icon text-info">
                  <i class="fas fa-file-alt"></i>
                </div>
                <div class="result-label">Pages</div>
                <div class="result-value">{{ scanResult.page_count }}</div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="result-card">
                <div class="result-icon text-secondary">
                  <i class="fas fa-clock"></i>
                </div>
                <div class="result-label">Scan Time</div>
                <div class="result-value">{{ scanResult.scan_time_ms }}ms</div>
              </div>
            </div>
          </div>

          <!-- Issue Counts -->
          <div class="row mb-4">
            <div class="col-md-4">
              <div class="issue-count critical">
                <span class="count">{{ scanResult.issue_count.critical }}</span>
                <span class="label">Critical Issues</span>
              </div>
            </div>
            <div class="col-md-4">
              <div class="issue-count major">
                <span class="count">{{ scanResult.issue_count.major }}</span>
                <span class="label">Major Issues</span>
              </div>
            </div>
            <div class="col-md-4">
              <div class="issue-count minor">
                <span class="count">{{ scanResult.issue_count.minor }}</span>
                <span class="label">Minor Issues</span>
              </div>
            </div>
          </div>

          <!-- Summary Text -->
          <div class="alert" :class="getSummaryAlertClass(scanResult.risk_level)">
            <i class="fas fa-info-circle me-2"></i>
            {{ scanResult.summary }}
          </div>

          <!-- Document Info -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-info-circle me-2"></i>
                Document Information
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <table class="table table-sm table-borderless mb-0">
                    <tr>
                      <td class="text-muted">File Name:</td>
                      <td>{{ scanResult.original_filename }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">File Size:</td>
                      <td>{{ formatFileSize(scanResult.file_size) }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Pages:</td>
                      <td>{{ scanResult.page_count }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Tagged:</td>
                      <td>
                        <span :class="scanResult.triage.is_tagged ? 'text-success' : 'text-danger'">
                          <i :class="scanResult.triage.is_tagged ? 'fas fa-check' : 'fas fa-times'"></i>
                          {{ scanResult.triage.is_tagged ? 'Yes' : 'No' }}
                        </span>
                      </td>
                    </tr>
                  </table>
                </div>
                <div class="col-md-6">
                  <table class="table table-sm table-borderless mb-0">
                    <tr>
                      <td class="text-muted">Scanned Image:</td>
                      <td>
                        <span :class="scanResult.triage.is_scanned ? 'text-warning' : 'text-success'">
                          <i :class="scanResult.triage.is_scanned ? 'fas fa-exclamation-triangle' : 'fas fa-check'"></i>
                          {{ scanResult.triage.is_scanned ? 'Yes (OCR needed)' : 'No' }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-muted">Word Count:</td>
                      <td>{{ scanResult.triage.word_count.toLocaleString() }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">Image Count:</td>
                      <td>{{ scanResult.triage.image_count }}</td>
                    </tr>
                    <tr>
                      <td class="text-muted">PDF/UA Compliant:</td>
                      <td>
                        <span :class="scanResult.validation.compliant ? 'text-success' : 'text-danger'">
                          <i :class="scanResult.validation.compliant ? 'fas fa-check-circle' : 'fas fa-times-circle'"></i>
                          {{ scanResult.validation.compliant ? 'Yes' : 'No' }}
                        </span>
                      </td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Issues List -->
          <div v-if="scanResult.issues.length > 0" class="card">
            <div class="card-header">
              <h6 class="mb-0">
                <i class="fas fa-exclamation-triangle me-2"></i>
                Issues Found ({{ scanResult.issues.length }})
              </h6>
            </div>
            <div class="card-body p-0">
              <div class="accordion" id="issuesAccordion">
                <div
                  v-for="(issue, index) in scanResult.issues"
                  :key="index"
                  class="accordion-item"
                >
                  <h2 class="accordion-header">
                    <button
                      class="accordion-button collapsed"
                      type="button"
                      :data-bs-toggle="'collapse'"
                      :data-bs-target="'#issue-' + index"
                    >
                      <span class="badge me-2" :class="getSeverityBadgeClass(issue.severity)">
                        {{ issue.severity }}
                      </span>
                      {{ issue.title }}
                      <span v-if="issue.occurrences > 1" class="badge bg-secondary ms-2">
                        {{ issue.occurrences }}x
                      </span>
                    </button>
                  </h2>
                  <div
                    :id="'issue-' + index"
                    class="accordion-collapse collapse"
                    data-bs-parent="#issuesAccordion"
                  >
                    <div class="accordion-body">
                      <div class="mb-3">
                        <h6 class="text-muted mb-1">What's wrong:</h6>
                        <p>{{ issue.plain_english }}</p>
                      </div>
                      <div class="mb-3">
                        <h6 class="text-muted mb-1">How to fix:</h6>
                        <p class="mb-0">{{ issue.how_to_fix }}</p>
                      </div>
                      <!-- Error Details -->
                      <div v-if="issue.error_details && issue.error_details.length > 0" class="mb-0">
                        <h6 class="text-muted mb-1">
                          Specific Issues ({{ issue.error_details.length }}):
                        </h6>
                        <ul class="list-group list-group-flush small">
                          <li
                            v-for="(detail, dIndex) in issue.error_details.slice(0, 5)"
                            :key="dIndex"
                            class="list-group-item py-2 px-0"
                          >
                            <div class="text-danger">{{ detail.message }}</div>
                            <div v-if="detail.context" class="text-muted font-monospace small">
                              {{ detail.context }}
                            </div>
                          </li>
                        </ul>
                        <p v-if="issue.error_details.length > 5" class="text-muted small mb-0 mt-2">
                          ... and {{ issue.error_details.length - 5 }} more
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Issues -->
          <div v-else class="alert alert-success">
            <i class="fas fa-check-circle me-2"></i>
            No accessibility issues detected. This PDF appears to meet basic accessibility requirements.
          </div>

          <!-- Actions -->
          <div class="mt-4 d-flex gap-2 flex-wrap">
            <button class="btn btn-outline-primary" @click="scanAnother">
              <i class="fas fa-redo me-2"></i>
              Scan Another PDF
            </button>
            <button class="btn btn-outline-secondary" @click="downloadReport">
              <i class="fas fa-download me-2"></i>
              Download Report
            </button>
            <button
              class="btn btn-success"
              @click="autoTagPdf"
              :disabled="isAutoTagging || isCheckingAccessibility"
              title="Use Adobe's AI to automatically add accessibility tags"
            >
              <span v-if="isAutoTagging">
                <i class="fas fa-spinner fa-spin me-2"></i>
                Auto-Tagging...
              </span>
              <span v-else>
                <i class="fas fa-magic me-2"></i>
                Auto-Tag PDF (Adobe)
              </span>
            </button>
            <button
              class="btn btn-info"
              @click="checkAccessibility"
              :disabled="isCheckingAccessibility || isAutoTagging"
              title="Get detailed report of what needs fixing for PDF/UA compliance"
            >
              <span v-if="isCheckingAccessibility">
                <i class="fas fa-spinner fa-spin me-2"></i>
                Checking...
              </span>
              <span v-else>
                <i class="fas fa-clipboard-check me-2"></i>
                PDF/UA Check (Adobe)
              </span>
            </button>
          </div>

          <!-- Auto-Tag Result -->
          <div v-if="autoTagResult" class="mt-4">
            <div class="alert alert-success">
              <h6 class="alert-heading">
                <i class="fas fa-check-circle me-2"></i>
                Auto-Tagging Complete!
              </h6>
              <p class="mb-2">Adobe's AI has automatically added accessibility tags to your PDF.</p>
              <hr>
              <div class="d-flex gap-2 flex-wrap">
                <button class="btn btn-success" @click="downloadTaggedPdf">
                  <i class="fas fa-download me-2"></i>
                  Download Tagged PDF
                  <small v-if="autoTagResult.tagged_pdf">({{ formatFileSize(autoTagResult.tagged_pdf.size) }})</small>
                </button>
                <button v-if="autoTagResult.report" class="btn btn-outline-info" @click="downloadAutoTagReport">
                  <i class="fas fa-file-excel me-2"></i>
                  Download Tagging Report
                </button>
                <button class="btn btn-outline-primary" @click="rescanTaggedPdf">
                  <i class="fas fa-search me-2"></i>
                  Scan Tagged PDF
                </button>
              </div>
            </div>
          </div>

          <!-- Accessibility Check Result -->
          <div v-if="accessibilityCheckResult" class="mt-4">
            <div class="card">
              <div class="card-header" :class="accessibilityCheckResult.summary && accessibilityCheckResult.summary.is_compliant ? 'bg-success text-white' : 'bg-warning'">
                <h6 class="mb-0">
                  <i :class="accessibilityCheckResult.summary && accessibilityCheckResult.summary.is_compliant ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'" class="me-2"></i>
                  Adobe PDF/UA Accessibility Check
                </h6>
              </div>
              <div class="card-body">
                <div v-if="accessibilityCheckResult.summary">
                  <!-- Compliance Status -->
                  <div class="mb-3">
                    <strong>PDF/UA Compliant:</strong>
                    <span :class="accessibilityCheckResult.summary.is_compliant ? 'text-success' : 'text-danger'" class="ms-2">
                      <i :class="accessibilityCheckResult.summary.is_compliant ? 'fas fa-check' : 'fas fa-times'"></i>
                      {{ accessibilityCheckResult.summary.is_compliant ? 'Yes' : 'No' }}
                    </span>
                  </div>

                  <!-- Issue Summary -->
                  <div v-if="accessibilityCheckResult.summary.total_issues > 0" class="mb-3">
                    <strong>Total Issues:</strong> {{ accessibilityCheckResult.summary.total_issues }}
                    <div class="row mt-2">
                      <div class="col-auto">
                        <span class="badge bg-danger">{{ accessibilityCheckResult.summary.issues_by_severity.critical || 0 }}</span> Critical
                      </div>
                      <div class="col-auto">
                        <span class="badge bg-warning text-dark">{{ accessibilityCheckResult.summary.issues_by_severity.serious || 0 }}</span> Serious
                      </div>
                      <div class="col-auto">
                        <span class="badge bg-info">{{ accessibilityCheckResult.summary.issues_by_severity.moderate || 0 }}</span> Moderate
                      </div>
                      <div class="col-auto">
                        <span class="badge bg-secondary">{{ accessibilityCheckResult.summary.issues_by_severity.minor || 0 }}</span> Minor
                      </div>
                    </div>
                  </div>

                  <!-- Issues List -->
                  <div v-if="accessibilityCheckResult.summary.issues && accessibilityCheckResult.summary.issues.length > 0">
                    <h6 class="mt-3 mb-2">Issues to Fix:</h6>
                    <div class="list-group">
                      <div
                        v-for="(issue, index) in accessibilityCheckResult.summary.issues"
                        :key="index"
                        class="list-group-item list-group-item-action"
                      >
                        <div class="d-flex w-100 justify-content-between">
                          <h6 class="mb-1">
                            <span class="badge me-2" :class="getIssueSeverityClass(issue.severity)">{{ issue.severity }}</span>
                            {{ issue.rule || issue.description }}
                          </h6>
                        </div>
                        <p class="mb-1 small">{{ issue.description }}</p>
                        <small v-if="issue.how_to_fix" class="text-success">
                          <i class="fas fa-wrench me-1"></i>
                          {{ issue.how_to_fix }}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div v-else class="alert alert-success mb-0">
                    <i class="fas fa-check-circle me-2"></i>
                    No accessibility issues found! This PDF meets PDF/UA requirements.
                  </div>
                </div>

                <div v-else class="text-muted">
                  <p>Raw report received. Check console for details.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'PdfScanner',

  data() {
    return {
      selectedFile: null,
      isDragging: false,
      isScanning: false,
      scanResult: null,
      error: null,
      // Auto-tag state
      isAutoTagging: false,
      autoTagResult: null,
      // Accessibility check state
      isCheckingAccessibility: false,
      accessibilityCheckResult: null,
    }
  },

  methods: {
    triggerFileInput() {
      if (!this.isScanning) {
        this.$refs.fileInput.click()
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      this.processFile(file)
    },

    handleDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      this.processFile(file)
    },

    processFile(file) {
      this.error = null
      this.scanResult = null

      if (!file) return

      if (file.type !== 'application/pdf') {
        this.error = 'Please select a PDF file'
        return
      }

      if (file.size > 50 * 1024 * 1024) {
        this.error = 'File size exceeds 50MB limit'
        return
      }

      this.selectedFile = file
    },

    clearFile() {
      this.selectedFile = null
      this.scanResult = null
      this.error = null
      this.autoTagResult = null
      this.accessibilityCheckResult = null
      this.$refs.fileInput.value = ''
    },

    async scanPdf() {
      if (!this.selectedFile) return

      this.isScanning = true
      this.error = null

      try {
        const formData = new FormData()
        formData.append('pdf', this.selectedFile)

        const response = await api.post('/juniper/ada-auditor/pdf/scan', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.data.success) {
          this.scanResult = response.data.data
        } else {
          this.error = response.data.message || 'Scan failed'
        }
      } catch (err) {
        console.error('PDF scan error:', err)
        this.error = err.response?.data?.message || 'Failed to scan PDF. Please try again.'
      } finally {
        this.isScanning = false
      }
    },

    scanAnother() {
      this.clearFile()
    },

    downloadReport() {
      // Create a simple text report
      const report = this.generateTextReport()
      const blob = new Blob([report], { type: 'text/plain' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `pdf-accessibility-report-${this.scanResult.original_filename}.txt`
      link.click()
      window.URL.revokeObjectURL(url)
    },

    generateTextReport() {
      const r = this.scanResult
      let report = `PDF ACCESSIBILITY REPORT
========================

File: ${r.original_filename}
Date: ${new Date().toLocaleString()}

SUMMARY
-------
Risk Level: ${this.formatRiskLevel(r.risk_level)}
PDF/UA Compliant: ${r.validation.compliant ? 'Yes' : 'No'}
Pages: ${r.page_count}
Tagged: ${r.triage.is_tagged ? 'Yes' : 'No'}
Scanned Image: ${r.triage.is_scanned ? 'Yes' : 'No'}

${r.summary}

ISSUE COUNTS
------------
Critical: ${r.issue_count.critical}
Major: ${r.issue_count.major}
Minor: ${r.issue_count.minor}

`

      if (r.issues.length > 0) {
        report += `ISSUES FOUND
------------
`
        r.issues.forEach((issue, i) => {
          report += `
${i + 1}. [${issue.severity.toUpperCase()}] ${issue.title}
   Rule: ${issue.rule_id}${issue.test_number ? ' (Test ' + issue.test_number + ')' : ''}
   Problem: ${issue.plain_english}
   Fix: ${issue.how_to_fix}
   Occurrences: ${issue.occurrences || 1}
`
          // Include error details if available
          if (issue.error_details && issue.error_details.length > 0) {
            report += `   Specific Issues:\n`
            issue.error_details.slice(0, 10).forEach((detail, j) => {
              report += `     ${j + 1}. ${detail.message}\n`
              if (detail.context) {
                report += `        Location: ${detail.context}\n`
              }
            })
            if (issue.error_details.length > 10) {
              report += `     ... and ${issue.error_details.length - 10} more\n`
            }
          }
        })
      }

      report += `
---
Generated by HeyADA Document Shield
`
      return report
    },

    async autoTagPdf() {
      if (!this.selectedFile) return

      this.isAutoTagging = true
      this.autoTagResult = null
      this.error = null

      try {
        const formData = new FormData()
        formData.append('pdf', this.selectedFile)
        formData.append('generate_report', 'true')

        const response = await api.post('/juniper/ada-auditor/pdf/auto-tag', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000 // 5 minutes timeout for large files
        })

        if (response.data.success) {
          this.autoTagResult = response.data
          if (this.$toast) this.$toast.success('PDF auto-tagged successfully!')
        } else {
          this.error = response.data.message || 'Auto-tagging failed'
          if (this.$toast) this.$toast.error(this.error)
        }
      } catch (err) {
        console.error('Auto-tag error:', err)
        this.error = err.response?.data?.message || 'Failed to auto-tag PDF. Please try again.'
        if (this.$toast) this.$toast.error(this.error)
      } finally {
        this.isAutoTagging = false
      }
    },

    downloadTaggedPdf() {
      if (!this.autoTagResult || !this.autoTagResult.tagged_pdf) return

      const taggedPdf = this.autoTagResult.tagged_pdf
      const byteCharacters = atob(taggedPdf.data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = taggedPdf.filename
      link.click()
      window.URL.revokeObjectURL(url)
    },

    downloadAutoTagReport() {
      if (!this.autoTagResult || !this.autoTagResult.report) return

      const report = this.autoTagResult.report
      const byteCharacters = atob(report.data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = report.filename
      link.click()
      window.URL.revokeObjectURL(url)
    },

    async rescanTaggedPdf() {
      if (!this.autoTagResult || !this.autoTagResult.tagged_pdf) return

      // Convert base64 to File object
      const taggedPdf = this.autoTagResult.tagged_pdf
      const byteCharacters = atob(taggedPdf.data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: 'application/pdf' })
      const file = new File([blob], taggedPdf.filename, { type: 'application/pdf' })

      // Set as selected file and scan
      this.selectedFile = file
      this.autoTagResult = null
      this.scanResult = null
      await this.scanPdf()
    },

    async checkAccessibility() {
      if (!this.selectedFile) return

      this.isCheckingAccessibility = true
      this.accessibilityCheckResult = null
      this.error = null

      try {
        const formData = new FormData()
        formData.append('pdf', this.selectedFile)

        const response = await api.post('/juniper/ada-auditor/pdf/accessibility-check', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000 // 5 minutes timeout
        })

        if (response.data.success) {
          this.accessibilityCheckResult = response.data
          console.log('Accessibility check result:', response.data)
          if (this.$toast) this.$toast.success('Accessibility check complete!')
        } else {
          this.error = response.data.message || 'Accessibility check failed'
          if (this.$toast) this.$toast.error(this.error)
        }
      } catch (err) {
        console.error('Accessibility check error:', err)
        this.error = err.response?.data?.message || 'Failed to check accessibility. Please try again.'
        if (this.$toast) this.$toast.error(this.error)
      } finally {
        this.isCheckingAccessibility = false
      }
    },

    getIssueSeverityClass(severity) {
      const classes = {
        critical: 'bg-danger',
        serious: 'bg-warning text-dark',
        moderate: 'bg-info',
        minor: 'bg-secondary'
      }
      return classes[(severity || '').toLowerCase()] || 'bg-secondary'
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    formatRiskLevel(level) {
      const labels = {
        critical: 'Critical - Requires Immediate Attention',
        high: 'High Risk',
        medium: 'Medium Risk',
        low: 'Low Risk',
        compliant: 'Compliant'
      }
      return labels[level] || level
    },

    getRiskClass(level) {
      const classes = {
        critical: 'risk-critical',
        high: 'risk-high',
        medium: 'risk-medium',
        low: 'risk-low',
        compliant: 'risk-compliant'
      }
      return classes[level] || ''
    },

    getRiskIcon(level) {
      const icons = {
        critical: 'fas fa-times-circle text-danger',
        high: 'fas fa-exclamation-circle text-danger',
        medium: 'fas fa-exclamation-triangle text-warning',
        low: 'fas fa-info-circle text-info',
        compliant: 'fas fa-check-circle text-success'
      }
      return icons[level] || 'fas fa-question-circle'
    },

    getSummaryAlertClass(level) {
      const classes = {
        critical: 'alert-danger',
        high: 'alert-danger',
        medium: 'alert-warning',
        low: 'alert-info',
        compliant: 'alert-success'
      }
      return classes[level] || 'alert-secondary'
    },

    getSeverityBadgeClass(severity) {
      const classes = {
        critical: 'bg-danger',
        major: 'bg-warning text-dark',
        minor: 'bg-info'
      }
      return classes[severity] || 'bg-secondary'
    }
  }
}
</script>

<style scoped>
.drop-zone {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #fafafa;
}

.drop-zone:hover {
  border-color: #007bff;
  background-color: #f0f7ff;
}

.drop-zone.drag-over {
  border-color: #007bff;
  background-color: #e7f1ff;
  transform: scale(1.01);
}

.drop-zone.has-file {
  border-style: solid;
  border-color: #28a745;
  background-color: #f0fff4;
}

.drop-zone.scanning {
  cursor: wait;
  border-color: #007bff;
  background-color: #f0f7ff;
}

.result-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  height: 100%;
}

.result-card .result-icon {
  font-size: 2rem;
  margin-bottom: 10px;
}

.result-card .result-label {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 5px;
}

.result-card .result-value {
  font-size: 1.25rem;
  font-weight: 600;
}

.result-card.risk-critical {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
}

.result-card.risk-critical .result-icon {
  color: #dc3545;
}

.result-card.risk-high {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
}

.result-card.risk-high .result-icon {
  color: #dc3545;
}

.result-card.risk-medium {
  background: #fff3cd;
  border: 1px solid #ffeeba;
}

.result-card.risk-medium .result-icon {
  color: #ffc107;
}

.result-card.risk-low {
  background: #d1ecf1;
  border: 1px solid #bee5eb;
}

.result-card.risk-low .result-icon {
  color: #17a2b8;
}

.result-card.risk-compliant {
  background: #d4edda;
  border: 1px solid #c3e6cb;
}

.result-card.risk-compliant .result-icon {
  color: #28a745;
}

.issue-count {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  border-radius: 8px;
  background: #f8f9fa;
}

.issue-count .count {
  font-size: 2rem;
  font-weight: 700;
}

.issue-count .label {
  font-size: 0.85rem;
  color: #6c757d;
}

.issue-count.critical .count {
  color: #dc3545;
}

.issue-count.major .count {
  color: #ffc107;
}

.issue-count.minor .count {
  color: #17a2b8;
}

.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
}
</style>
